/**
 * @fileoverview Edit User dialog hook.
 * @description The Edit dialog's data and command: reads the user's details afresh (whose ETag the
 * update carries as If-Match) and saves the changed fields. Saving with nothing changed just closes.
 * The confirmation and the close run in the mutation's own callback, so they still happen if the
 * dialog unmounts while the request is in flight, and closing is held while the request is pending.
 * A stale-version refusal (412 / 3022) re-reads the details so the form refills with them.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useFreshUserDetail } from '@/features/users/hooks/useFreshUserDetail'
import { useUpdateUser } from '@/features/users/hooks/useUpdateUser'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import type { UpdateUserRequest, UserDetail } from '@/features/users/types/users.types'

interface EditUserDialogOptions {
  userId: string
  onClose: () => void
  onUpdated: (user: UserDetail) => void
}

/**
 * @description Loads what the Edit dialog needs and runs the update.
 * @param {EditUserDialogOptions} options - The user, and the dialog's callbacks.
 * @param {string} options.userId - The user to edit.
 * @param {() => void} options.onClose - Dismisses the dialog.
 * @param {(user: UserDetail) => void} options.onUpdated - Called with the updated user after a
 * successful save, before the dialog closes.
 * @returns {object} The details, the close handler (held while the save is pending), and the save
 * command with its state.
 */
export function useEditUserDialog({ userId, onClose, onUpdated }: EditUserDialogOptions) {
  const detailQuery = useFreshUserDetail(userId)
  const updateUser = useUpdateUser({
    /**
     * @description Reports the updated user, then closes the dialog. Runs even when the dialog
     * has unmounted since the request was sent.
     * @param {VersionedUserDetail} result - The updated user and their new version.
     */
    onSuccess: ({ user }) => {
      onUpdated(user)
      onClose()
    },
  })
  const close = useGuardedClose(onClose, updateUser.isPending)
  const detail = detailQuery.detail

  /**
   * @description Saves the changed fields against the version the form was filled from, or just
   * closes when nothing changed.
   * @param {UpdateUserRequest} payload - The changed fields.
   */
  const submit = (payload: UpdateUserRequest) => {
    if (!detail || Object.keys(payload).length === 0) {
      onClose()
      return
    }
    updateUser.mutate(
      { userId, payload, etag: detail.etag },
      {
        /**
         * @description Re-reads the user when someone else changed them since the dialog loaded.
         * @param {ApiError} error - The refusal.
         */
        onError: (error) => {
          if (error.code === ERROR_CODES.VERSION_CONFLICT) {
            void detailQuery.refetch()
          }
        },
      },
    )
  }

  return {
    detail,
    isLoading: detailQuery.isPending,
    loadError: detailQuery.error,
    close,
    submit,
    isSaving: updateUser.isPending,
    saveError: updateUser.error,
  }
}
